import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { translateOutsideReact } from '@/i18n/translateOutsideReact';
import { handleError } from '@/utils/handleError';

// caller-access-context.AC13 (issue #628) — see accountApiClient.ts for the rationale.
// src/ has no BFF hop, so the browser's own request already carries the real IP/UA that
// kyc-api's extractCallerContext forwards to the banking provider; this header just declares
// the claim honestly instead of leaving it to extractCallerContext's stricter default.
const CALLER_ACCESS_MODE_HEADERS = { "x-caller-access-mode": "user-present" } as const;

// KYC provider vocabulary — single source of truth for src/ (kycService.ts and KycModal.tsx
// import this rather than re-declaring their own union, per the "source domain union values,
// never hand-type/copy-paste" convention in .claude/rules/engineering-practice.md). Matches
// packages/api/types.ts's KycProvider (that file's canonical home is apps/client's BFF layer;
// src/ isn't wired onto @financialharmony/api at all today, so it keeps its own copy here
// rather than taking on that dependency for a single type import).
export type KycProvider = 'openpayments' | 'ztl';

// KYC types for frontend
export interface KycRecord {
  id: string;
  company_id: string;
  provider: KycProvider;
  type: 'payment' | 'onboarding';
  status: 'pending' | 'completed' | 'failed' | 'invalid';
  created_at: string;
  resource_id?: string;
  extra_data?: {
    kyc_url?: string;
    [key: string]: unknown;
  };
  valid_until?: string;
}

export interface CreateKycRequest {
  provider: KycProvider;
  type: 'payment' | 'onboarding';
  countryCode?: string;
}

export interface CreateKycResponse {
  url: string;
}

/**
 * Client for interacting with the kyc-api edge function
 */
export class KycApiClient {
  /**
   * Create a new KYC for a company
   * @param companyId Company ID
   * @param request KYC creation request
   * @returns Promise with KYC URL
   */
  static async createKyc(companyId: string, request: CreateKycRequest): Promise<CreateKycResponse> {
    try {
      console.log(`Creating KYC for company ${companyId}:`, request);

      const { data, error } = await supabase.functions.invoke('kyc-api', {
        body: {
          action: 'createKyc',
          companyId,
          ...request
        },
        headers: CALLER_ACCESS_MODE_HEADERS,
      });

      if (error) throw error;

      console.log('KYC created successfully:', data);
      toast.success(translateOutsideReact('consultantToasts.api.kycInitiated'));
      return data;
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToCreateKyc'), 'createKyc');
      throw error;
    }
  }

  /**
   * Get a specific KYC by ID
   * @param companyId Company ID
   * @param kycId KYC ID
   * @returns Promise with the KYC record
   */
  static async getKyc(companyId: string, kycId: string): Promise<KycRecord> {
    try {
      console.log(`Getting KYC ${kycId} for company ${companyId}`);

      const { data, error } = await supabase.functions.invoke('kyc-api', {
        body: {
          action: 'getKyc',
          companyId,
          kycId
        },
        headers: CALLER_ACCESS_MODE_HEADERS,
      });

      if (error) throw error;

      console.log('KYC retrieved successfully:', data);
      return data;
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToGetKyc'), 'getKyc');
      throw error;
    }
  }

  /**
   * List KYCs for a company
   * @param companyId Company ID
   * @param latest Whether to get only the latest KYC
   * @returns Promise with list of KYC records
   */
  static async listKycs(companyId: string): Promise<KycRecord[]> {
    try {
      console.log(`Listing KYCs for company ${companyId}`);

      const { data, error } = await supabase.functions.invoke('kyc-api', {
        body: {
          action: 'listKycs',
          companyId,
        },
        headers: CALLER_ACCESS_MODE_HEADERS,
      });

      if (error) throw error;

      console.log(`KYCs retrieved for company ${companyId}:`, data);
      return data;
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToListKycs'), 'listKycs');
      throw error;
    }
  }

  /**
   * Get the latest KYC for a company by provider and type
   * @param companyId Company ID
   * @param provider KYC provider
   * @param type KYC type
   * @returns Promise with the latest KYC record or null
   */
  static async getLatestKyc(
    companyId: string,
    provider: KycProvider = 'openpayments',
    type: 'payment' | 'onboarding' = 'payment'
  ): Promise<KycRecord | null> {
    try {
      const kycs = await this.listKycs(companyId);

      // Filter by provider and type
      const matchingKycs = kycs.filter(kyc =>
        kyc.provider === provider && kyc.type === type
      );

      return matchingKycs.length > 0 ? matchingKycs[0] : null;
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToGetLatestKyc'), 'getLatestKyc');
      throw error;
    }
  }

  /**
   * Check if a KYC is valid (completed and not expired)
   * @param kyc KYC record
   * @returns Whether the KYC is valid
   */
  static isKycValid(kyc: KycRecord): boolean {
    if (kyc.status !== 'completed') {
      return false;
    }

    if (kyc.valid_until) {
      const now = new Date();
      const validUntil = new Date(kyc.valid_until);
      return now < validUntil;
    }

    // If no expiry date, consider it valid if status is completed
    return true;
  }

  /**
   * Check if a KYC needs refresh (expired or invalid status)
   * @param kyc KYC record
   * @returns Whether the KYC needs refresh
   */
  static needsRefresh(kyc: KycRecord): boolean {
    if (kyc.status === 'invalid' || kyc.status === 'failed') {
      return true;
    }

    if (kyc.valid_until) {
      const now = new Date();
      const validUntil = new Date(kyc.valid_until);
      return now >= validUntil;
    }

    return false;
  }
}